import React, { useEffect, useState } from 'react';
import { Lead, LeadsResponse, LeadFilters } from '../interfaces/Lead';
import { leadService } from '../services/leadService';
import { useAuth } from '../contexts/AuthContext';
import { Filter, Download } from 'lucide-react';
import styles from './Leads.module.css';
import { DefaultLoader } from '../components';

// --- COMPONENTE AUXILIAR PARA CLIQUE E CÓPIA ---
interface ClickToCopyProps {
  text: string | number | null | undefined;
  children: React.ReactNode;
}

const ClickToCopy: React.FC<ClickToCopyProps> = ({ text, children }) => {
  const [copied, setCopied] = useState(false);

  if (!text) return <>{children}</>;

  const handleCopy = async (e: React.MouseEvent) => {
    // Evita conflitos caso tenha algum link por perto
    e.stopPropagation(); 
    try {
      await navigator.clipboard.writeText(String(text));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500); // Some após 1.5 segundos
    } catch (err) {
      console.error('Erro ao copiar text: ', err);
    }
  };

  return (
    <span 
      className={styles.copyableContainer} 
      onClick={handleCopy}
      title="Clique para copiar"
    >
      {children}
      <span className={`${styles.copyTooltip} ${copied ? styles.copyTooltipShow : ''}`}>
        Copiado!
      </span>
    </span>
  );
};
// -----------------------------------------------

const Leads: React.FC = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 50, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<LeadFilters>({});

  const fetchLeads = async (page: number = 1) => {
    try {
      setLoading(true);
      setError('');
      const response: LeadsResponse = await leadService.getLeads(page, 50, filters);
      setLeads(response.data);
      setMeta(response.meta);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [filters]);

  const handleFilterChange = (newFilters: Partial<LeadFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const clearFilters = () => {
    setFilters({});
  };

  const exportToCSV = () => {
    const headers = ['ID', 'Nome', 'Email', 'WhatsApp', 'Origem', 'Destino', 'Motivo', 'Preferência', 'Contatado', 'Contatado Por', 'Data Contato', 'Data Criação', 'Google Gclid'];
    const rows = leads.map(lead => [
      lead.id,
      lead.name || '',
      lead.email || '',
      lead.gclid || '-',
      lead.whatsapp || '',
      lead.origin || '',
      lead.destination || '',
      formatTravelReason(lead.travel_reason),
      lead.contact_preference || '',
      lead.contacted ? 'Sim' : 'Não',
      lead.contacted_by || '',
      lead.contacted_at ? formatDate(lead.contacted_at) : '',
      formatDate(lead.created_at)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `leads_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTravelReason = (reason: string | null) => {
    const reasons: Record<string, string> = {
      'lazer': 'Lazer',
      'trabalho': 'Trabalho',
      'familia': 'Família',
      'outros': 'Outros'
    };
    return reason ? reasons[reason] || reason : '-';
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja deletar este lead?')) return;
    
    try {
      await leadService.deleteLead(id);
      fetchLeads(meta.page);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Erro ao deletar lead');
    }
  };

  const handleToggleContacted = async (id: number, currentStatus: boolean) => {
    if (!user) return;
    
    try {
      await leadService.updateContacted(id, !currentStatus, user.username);
      fetchLeads(meta.page);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Erro ao atualizar status');
    }
  };

  if (loading) {
    return <DefaultLoader title="Leads" messages={['Carregando leads...', 'Processando contatos...', 'Organizando dados...']} />;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Leads</h1>
        <div className={styles.headerActions}>
          <button
            className="btn btn-secondary"
            onClick={() => setShowFilters(!showFilters)}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Filter size={16} />
            {showFilters ? 'Ocultar' : 'Mostrar'} Filtros
          </button>
          <button
            className="btn btn-primary"
            onClick={exportToCSV}
            disabled={leads.length === 0}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Download size={16} />
            Exportar CSV
          </button>
        </div>
      </div>

      {showFilters && (
        <div className={styles.filterCard}>
          <div className={styles.filterGrid}>
            <div className={styles.filterField}>
              <label className={styles.filterLabel}>Email</label>
              <input
                type="text"
                value={filters.email || ''}
                onChange={(e) => handleFilterChange({ email: e.target.value || undefined })}
                className="input"
                placeholder="Buscar por email"
              />
            </div>

            <div className={styles.filterField}>
              <label className={styles.filterLabel}>WhatsApp</label>
              <input
                type="text"
                value={filters.whatsapp || ''}
                onChange={(e) => handleFilterChange({ whatsapp: e.target.value || undefined })}
                className="input"
                placeholder="Buscar por WhatsApp"
              />
            </div>

            <div className={styles.filterField}>
              <label className={styles.filterLabel}>Tem Email?</label>
              <select
                value={filters.hasEmail === undefined ? '' : filters.hasEmail.toString()}
                onChange={(e) => handleFilterChange({ 
                  hasEmail: e.target.value === '' ? undefined : e.target.value === 'true' 
                })}
                className="input"
              >
                <option value="">Todos</option>
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </select>
            </div>

            <div className={styles.filterField}>
              <label className={styles.filterLabel}>Tem WhatsApp?</label>
              <select
                value={filters.hasWhatsapp === undefined ? '' : filters.hasWhatsapp.toString()}
                onChange={(e) => handleFilterChange({ 
                  hasWhatsapp: e.target.value === '' ? undefined : e.target.value === 'true' 
                })}
                className="input"
              >
                <option value="">Todos</option>
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </select>
            </div>

            <div className={styles.filterField}>
              <label className={styles.filterLabel}>Data Inicial</label>
              <input
                type="date"
                value={filters.startDate || ''}
                onChange={(e) => handleFilterChange({ startDate: e.target.value || undefined })}
                className="input"
              />
            </div>

            <div className={styles.filterField}>
              <label className={styles.filterLabel}>Data Final</label>
              <input
                type="date"
                value={filters.endDate || ''}
                onChange={(e) => handleFilterChange({ endDate: e.target.value || undefined })}
                className="input"
              />
            </div>

            <div className={styles.filterActions}>
              <button className="btn btn-secondary" onClick={clearFilters}>
                Limpar Filtros
              </button>
            </div>
          </div>

          <div className={styles.filterSummary}>
            <span>Total de Leads: <strong>{meta.total}</strong></span>
          </div>
        </div>
      )}

      {/* Desktop: Tabela */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.colId}>ID</th>
              <th className={styles.colName}>Nome</th>
              <th className={styles.colEmail}>Email</th>
              <th className={styles.colOrigin}>Origem</th>
              <th className={styles.colDestination}>Destino</th>
              <th className={styles.colReason}>Motivo</th>
              <th className={styles.colPreference}>Pref.</th>
              <th className={styles.colWhatsapp}>WhatsApp</th>
              <th className={styles.colContacted}>Contatado</th>
              <th className={styles.colContactedBy}>Por</th>
              <th className={styles.colDate}>Data</th>
              <th className={styles.colGclid}>Gclid</th>
              <th className={styles.colActions}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((lead) => (
              <tr key={lead.id}>
                <td><ClickToCopy text={lead.id}>{lead.id}</ClickToCopy></td>
                <td><ClickToCopy text={lead.name}>{lead.name || <span className={styles.emptyCell}>-</span>}</ClickToCopy></td>
                <td><ClickToCopy text={lead.email}>{lead.email || <span className={styles.emptyCell}>-</span>}</ClickToCopy></td>
                <td><ClickToCopy text={lead.origin}>{lead.origin || <span className={styles.emptyCell}>-</span>}</ClickToCopy></td>
                <td><ClickToCopy text={lead.destination}>{lead.destination || <span className={styles.emptyCell}>-</span>}</ClickToCopy></td>
                <td>{formatTravelReason(lead.travel_reason)}</td>
                <td>
                  {lead.contact_preference === 'email' && <span className={`${styles.badge} ${styles.badgeEmail}`}>Email</span>}
                  {lead.contact_preference === 'whatsapp' && <span className={`${styles.badge} ${styles.badgeWhatsapp}`}>WhatsApp</span>}
                  {!lead.contact_preference && <span className={styles.emptyCell}>-</span>}
                </td>
                <td>
                  {lead.whatsapp ? (
                    <a href={`https://wa.me/${lead.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className={styles.whatsappLink}>
                      {lead.whatsapp}
                    </a>
                  ) : <span className={styles.emptyCell}>-</span>}
                </td>
                <td>
                  <button className={`${styles.contactedButton} ${lead.contacted ? styles.contacted : styles.notContacted}`} onClick={() => handleToggleContacted(lead.id, lead.contacted)}>
                    {lead.contacted ? '✓' : '✗'}
                  </button>
                </td>
                <td>
                  {lead.contacted && lead.contacted_by ? (
                    <div className={styles.contactedBy}>
                      <span className={styles.userName}>{lead.contacted_by}</span>
                      {lead.contacted_at && <span className={styles.contactedDate}>{formatDate(lead.contacted_at)}</span>}
                    </div>
                  ) : <span className={styles.emptyCell}>-</span>}
                </td>
                <td><ClickToCopy text={formatDate(lead.created_at)}>{formatDate(lead.created_at)}</ClickToCopy></td>
                <td>
                  {lead.gclid ? (
                    <ClickToCopy text={lead.gclid}><span className={styles.gclick}>{lead.gclid}</span></ClickToCopy>
                  ) : <span className={styles.emptyCell}>-</span>}
                </td>
                <td><button className={styles.deleteButton} onClick={() => handleDelete(lead.id)}>Deletar</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: Cards */}
      <div className={styles.cardList}>
        {leads.map((lead) => (
          <div key={lead.id} className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.cardName}>{lead.name || 'Sem nome'}</span>
              <span className={styles.cardId}>#{lead.id}</span>
            </div>
            <div className={styles.cardGrid}>
              <div className={styles.cardField}>
                <span className={styles.cardLabel}>Email</span>
                <span className={styles.cardValue}>{lead.email || '-'}</span>
              </div>
              <div className={styles.cardField}>
                <span className={styles.cardLabel}>WhatsApp</span>
                <span className={styles.cardValue}>
                  {lead.whatsapp ? (
                    <a href={`https://wa.me/${lead.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className={styles.whatsappLink}>
                      {lead.whatsapp}
                    </a>
                  ) : '-'}
                </span>
              </div>
              <div className={styles.cardField}>
                <span className={styles.cardLabel}>Origem</span>
                <span className={styles.cardValue}>{lead.origin || '-'}</span>
              </div>
              <div className={styles.cardField}>
                <span className={styles.cardLabel}>Destino</span>
                <span className={styles.cardValue}>{lead.destination || '-'}</span>
              </div>
              <div className={styles.cardField}>
                <span className={styles.cardLabel}>Motivo</span>
                <span className={styles.cardValue}>{formatTravelReason(lead.travel_reason)}</span>
              </div>
              <div className={styles.cardField}>
                <span className={styles.cardLabel}>Data</span>
                <span className={styles.cardValue}>{formatDate(lead.created_at)}</span>
              </div>
              {lead.contacted && lead.contacted_by && (
                <div className={styles.cardField}>
                  <span className={styles.cardLabel}>Contatado por</span>
                  <span className={styles.cardValue}>{lead.contacted_by}</span>
                </div>
              )}
              {lead.gclid && (
                <div className={styles.cardField}>
                  <span className={styles.cardLabel}>Gclid</span>
                  <span className={styles.cardValue}>{lead.gclid}</span>
                </div>
              )}
            </div>
            <div className={styles.cardActions}>
              <button className={`${styles.contactedButton} ${lead.contacted ? styles.contacted : styles.notContacted}`} onClick={() => handleToggleContacted(lead.id, lead.contacted)}>
                {lead.contacted ? '✓ Contatado' : '✗ Não contatado'}
              </button>
              <button className={styles.deleteButton} onClick={() => handleDelete(lead.id)}>Deletar</button>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.pagination}>
        <div className={styles.paginationInfo}>
          Página {meta.page} de {meta.totalPages} ({meta.total} leads)
        </div>
        <div className={styles.paginationButtons}>
          <button className={styles.pageButton} onClick={() => fetchLeads(meta.page - 1)} disabled={meta.page === 1}>
            Anterior
          </button>
          <button className={styles.pageButton} onClick={() => fetchLeads(meta.page + 1)} disabled={meta.page === meta.totalPages}>
            Próxima
          </button>
        </div>
      </div>
    </div>
  );
};

export default Leads;